import { FC, useMemo, useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';

import Page from 'modules/_shared/components/Pages/Page';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import PageListHeader from 'modules/_shared/components/Headers/PageListHeader';
import Button from 'modules/_shared/components/Buttons';
import GoBackButton from 'modules/_shared/components/Buttons/GoBackButton';
import Breadcrumb, {
  BreadcrumbItem,
} from 'modules/_shared/components/Breadcrumb';
import { addAlert } from 'modules/_shared/redux/alertActions';
import alertBodyTypes from 'modules/_shared/components/Alert/alertBodyTypes';

import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import menuTypes from 'constants/menuTypes';

import {
  IncentiveOfferOperation,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';

import { State } from 'redux/initialState';
import { useIncentivesMarket } from '../../contexts/IncentivesMarketContext';
import IncentivesMarketAdminPanelOperationsTable from '../../components/IncentivesMarketAdminPanelOperationsTable';
import { getIncentivesMarketHomePath } from '../../utils/getIncentivesMarketHomePath';

import '../../styles/IncentivesMarket.scss';

const PENDING_FOR_USER_STATUSES = ['KYC_REVIEW', 'PAYMENT_PENDING'];

const getPartyId = (party: string | Partial<IncentivesMarketBeneficiary>): string | null => {
  if (!party) return null;
  if (typeof party === 'string') return party;
  return (party as IncentivesMarketBeneficiary)?._id ?? null;
};

const isBeneficiaryOperation = (
  operation: IncentiveOfferOperation,
  currentBeneficiaryId: string
): boolean => {
  const buyerId = getPartyId(operation.buyer);
  const sellerId = getPartyId(operation.seller);
  return buyerId === currentBeneficiaryId || sellerId === currentBeneficiaryId;
};

const isPendingForUser = (op: IncentiveOfferOperation): boolean =>
  PENDING_FOR_USER_STATUSES.includes(op.status);

const hasPendingOperationsForPartner = (
  operations: IncentiveOfferOperation[],
  beneficiaryId: string | null
): boolean => {
  if (!beneficiaryId) return false;
  return operations.some(
    (op) => isBeneficiaryOperation(op, beneficiaryId) && isPendingForUser(op)
  );
};

const IncentiveOffersMyOperations: FC = () => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { data } = useIncentivesMarket();
  const hasShownPendingAlert = useRef(false);

  const queryParams = new URLSearchParams(location.search);
  const beneficiaryId = queryParams.get('beneficiaryId');
  const beneficiaryQuery = beneficiaryId ? `?beneficiaryId=${beneficiaryId}` : '';
  const incentivesMarketHomePath = getIncentivesMarketHomePath(data.societyId, {
    beneficiaryQuery,
    isBeneficiaryView: Boolean(beneficiaryId),
  });

  const allOperations = useSelector(
    ({ incentiveOfferOperations }: State) =>
      (incentiveOfferOperations ?? []) as IncentiveOfferOperation[]
  );

  useGetMenu(menuTypes.NULL);

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('Dashboard'), to: '/' },
      { label: data.societyName, to: `/sociedad/${data.societyId}` },
      {
        label: t('IncentivesMarket'),
        to: incentivesMarketHomePath,
      },
      { label: t('MyOperations'), isActive: true },
    ],
    [t, data.societyName, data.societyId, incentivesMarketHomePath]
  );

  const hasPendingOperations = useMemo(
    () => hasPendingOperationsForPartner(allOperations, beneficiaryId),
    [allOperations, beneficiaryId]
  );

  useEffect(() => {
    if (hasPendingOperations && !hasShownPendingAlert.current) {
      hasShownPendingAlert.current = true;
      dispatch(addAlert(alertBodyTypes.MY_OPERATIONS_PENDING_ATTENTION));
    }
  }, [hasPendingOperations, dispatch]);

  const handleGoToOperationDetail = useCallback(
    (operationId: string) => {
      history.push(
        `/detalle-operacion-incentivos/${data.societyId}/${operationId}${beneficiaryQuery}`
      );
    },
    [history, data.societyId, beneficiaryQuery]
  );

  const handleGoToMarket = useCallback(() => {
    history.push(incentivesMarketHomePath);
  }, [history, incentivesMarketHomePath]);

  const pageActions = [
    <GoBackButton key="go-back" icon="ni-arrow-left" />,
    <Button key="go-to-market" onClick={handleGoToMarket}>
      <span>{t('GoToMarket')}</span>
    </Button>,
  ];

  if (data.isLoading) return <CustomLoading />;

  return (
    <Page id="trading-my-operations-page" className="pb-5">
      <div className="nk-block-head-content mb-3">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <PageListHeader
        subTitle={t('MyOperations')}
        description={t('MyOperationsListDescription')}
        actions={pageActions}
      />

      <div className="nk-block nk-block-lg">
        <div className="card card-bordered">
          <div className="card-inner">
            <h6 className="card-title mb-3">
              <em className="icon ni ni-list text-primary mr-2" />
              {t('OperationsList')}
            </h6>

            <p className="text-soft fs-12px mb-3">
              <em className="icon ni ni-info text-soft mr-1" />
              {t('MyOperationsListHint')}
            </p>
          </div>

          <div className="card-inner p-0">
            <IncentivesMarketAdminPanelOperationsTable
              operations={allOperations}
              onViewOperationDetail={handleGoToOperationDetail}
              variant="partner"
              beneficiaryId={beneficiaryId}
              embedded
            />
          </div>

          <div className="card-inner bg-lighter border-top">
            <button
              type="button"
              className="btn btn-outline-primary mr-2"
              onClick={() => history.push(incentivesMarketHomePath)}
            >
              {t('MyOperationsSummary')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleGoToMarket}
            >
              {t('GoToMarket')}
            </button>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default IncentiveOffersMyOperations;
